/**
 * A paged list asked for a page past its last one (#165).
 *
 * The server answers an empty page, and a screen that reads "no rows" as "nothing here" shows its
 * empty state over a list that has rows: after an old link, or when the last row of the last page
 * goes away. #159 fixed the address book for itself; this is that fix, shared by every paged list.
 *
 * When the list has rows, and the page shown is past its last page, and is the page the URL asks
 * for, the screen steps back to the last page. Through `replace`, which is the caller's to pass in
 * [stepTo], so Back does not return to the empty page and step forward again.
 *
 * `shown.page === asked` is what keeps a page still loading from stepping anywhere: a query that
 * holds the previous page's answer while the next one loads would otherwise compare an old page
 * against the new URL.
 */

import { useEffect, useRef } from 'react'

export interface ShownPage {
  /** The page the rows on screen are, as the answer says. */
  page: number
  /** Rows in the whole list, not on this page. */
  total: number
  totalPages: number
}

export function useStepBackPastEnd(shown: ShownPage | undefined, asked: number, stepTo: (page: number) => void): void {
  // The latest navigation, without making a caller's inline closure re-run the effect each render.
  const step = useRef(stepTo)
  step.current = stepTo

  const page = shown?.page
  const total = shown?.total
  const totalPages = shown?.totalPages
  useEffect(() => {
    if (page === undefined || total === undefined || totalPages === undefined) return
    if (total > 0 && page > totalPages && page === asked) step.current(totalPages)
  }, [page, total, totalPages, asked])
}
